import { useState, useCallback } from 'react';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { useToast } from '@/hooks/useToast';
import { createClient } from '@/lib/supabase/browser';
import { getBaseUrl } from '@/lib/utils/getBaseUrl';
import { normalizePostInteraction } from '@/lib/row-utils';

export interface PostLike {
  id: string;
  post_id?: string;
  profile_post_id?: string;
  user_id: string;
  created_at: string;
  user?: {
    name: string;
    avatar: string;
  };
}

export interface PostShare {
  id: string;
  original_post_id?: string;
  original_profile_post_id?: string;
  shared_by_user_id: string;
  shared_at: string;
  user?: {
    name: string;
    avatar: string;
  };
}

export function usePostInteractions() {
  const { user } = useAuthContext();
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);

  // Toggle like on a post
  const toggleLike = useCallback(async (postId: string, isProfilePost = false) => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const supabase = createClient();
      
      // Check if user already liked the post
      const { data: existingLike, error: checkError } = await supabase
        .from('post_likes')
        .select('id')
        .eq('id', user.id)
        .eq(isProfilePost ? 'profile_post_id' : 'post_id', postId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingLike) {
        // Unlike the post
        const { error: unlikeError } = await supabase
          .from('post_likes')
          .delete()
          .eq('id', existingLike.id as string);

        if (unlikeError) throw unlikeError;

        toast({
          title: "Post unliked",
          description: "You've unliked this post",
          variant: "success",
        });
      } else {
        // Like the post
        const { error: likeError } = await supabase
          .from('post_likes')
          .insert({
            id: user.id,
            [isProfilePost ? 'profile_post_id' : 'post_id']: postId,
          });

        if (likeError) throw likeError;

        toast({
          title: "Post liked",
          description: "You've liked this post",
          variant: "success",
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast({
        title: "Error",
        description: "Failed to update like status",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, toast]);

  // Get likes for a post
  const getLikes = useCallback(async (postId: string, isProfilePost = false) => {
    try {
      const supabase = createClient();
      const { data: likesRaw, error: likesErr } = await supabase
        .from('post_likes')
        // prefer joined user, but also select scalar fields so we can still render
        .select('id,user_id,created_at, user:profiles(username,full_name,avatar_url)')
        .eq(isProfilePost ? 'profile_post_id' : 'post_id', postId)
        .order('created_at', { ascending: false });

      if (likesErr) {
        console.error('[PostInteractions] likes error', likesErr);
        return [];
      }

      // strip any SelectQueryError and coerce to PostLike[]
      const likes = (likesRaw ?? []).map((row: any) => ({
        id: row.id,
        user_id: row.user_id,
        created_at: row.created_at,
        user: row.user && !row.user?.hint ? row.user : undefined,
      })) as unknown as PostLike[];
      
      return likes;
    } catch (error) {
      console.error('Error getting likes:', error);
      return [];
    }
  }, []);

  // Check if user has liked a post
  const hasLiked = useCallback(async (postId: string, isProfilePost = false) => {
    if (!user?.id) return false;

    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('post_likes')
        .select('id')
        .eq('id', user.id)
        .eq(isProfilePost ? 'profile_post_id' : 'post_id', postId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return !!data;
    } catch (error) {
      console.error('Error checking like status:', error);
      return false;
    }
  }, [user?.id]);

  // Share a post
  const sharePost = useCallback(async (originalPostId: string, isProfilePost = false) => {
    if (!user?.id) return;

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('post_shares')
        .insert({
          original_post_id: isProfilePost ? undefined : originalPostId,
          original_profile_post_id: isProfilePost ? originalPostId : undefined,
          shared_by_user_id: user.id,
        });

      if (error) throw error;

      toast({
        title: "Post shared",
        description: "You've shared this post",
        variant: "success",
      });
    } catch (error) {
      console.error('Error sharing post:', error);
      toast({
        title: "Error",
        description: "Failed to share post",
        variant: "destructive",
      });
    }
  }, [user?.id, toast]);

  // Get shares for a post
  const getShares = useCallback(async (postId: string, isProfilePost = false) => {
    try {
      const supabase = createClient();
      const { data: sharesRaw, error: sharesErr } = await supabase
        .from('post_shares')
        .select('id,shared_by_user_id as user_id,shared_at as created_at, user:profiles(username,full_name,avatar_url)')
        .eq(isProfilePost ? 'original_profile_post_id' : 'original_post_id', postId)
        .order('shared_at', { ascending: false });

      if (sharesErr) {
        console.error('[PostInteractions] shares error', sharesErr);
        return [];
      }

      const shares = (sharesRaw ?? []).map((row: any) => ({
        id: row.id,
        user_id: row.user_id,
        created_at: row.created_at,
        user: row.user && !row.user?.hint ? row.user : undefined,
      })) as unknown as PostShare[];
      
      return shares;
    } catch (error) {
      console.error('Error getting shares:', error);
      return [];
    }
  }, []);

  // Copy post link to clipboard
  const copyPostLink = useCallback(async (postId: string, isProfilePost = false) => {
    try {
      const baseUrl = getBaseUrl();
      const postUrl = isProfilePost 
        ? `${baseUrl}/profile/posts/${postId}`
        : `${baseUrl}/community/posts/${postId}`;
      
      await navigator.clipboard.writeText(postUrl);
      
      toast({
        title: "Link copied",
        description: "Post link copied to clipboard",
        variant: "success",
      });
    } catch (error) {
      console.error('Error copying link:', error);
      toast({
        title: "Error",
        description: "Failed to copy link",
        variant: "destructive",
      });
    }
  }, [toast]);

  return {
    isLoading,
    toggleLike,
    getLikes,
    hasUserLiked: hasLiked,
    sharePost,
    getShares,
    copyPostLink,
  };
} 